"use client";

import { isChunkLoadError, safeReload } from "@/lib/chunk-error-recovery";
import { useEffect } from "react";

/**
 * Railway replaces the b3os-web container on every deploy, and each build's
 * `.next/static/chunks/*` hashes are unique. A browser tab that was loaded
 * against the old build and navigates after the deploy will request a chunk
 * hash that no longer exists — the origin returns 404, turbopack raises
 * ChunkLoadError, and the user lands on Next's default "Application error"
 * page. Reload so the fresh HTML can hand out the new chunk hashes.
 *
 * Skipped in dev to avoid stomping HMR failures.
 */
export function ChunkErrorRecovery(): null {
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;

    const onError = (event: ErrorEvent) => {
      if (!isChunkLoadError(event.error)) return;
      safeReload("window.error");
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (!isChunkLoadError(event.reason)) return;
      safeReload("window.unhandledrejection");
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
    };
  }, []);

  return null;
}
